"use client"

import { useState } from "react"
import Link from "next/link"
import { useLocale } from "next-intl"
import { cn } from "@/lib/utils"
import { useTranslations } from "@/lib/intl/use-translations"
import {
  type TestData,
  TEST_KEY_PREFIX,
  MEMBERSHIP_ANNUAL_CHF,
} from "@/data/tests"
import { InfoIcon } from "@/components/ui/icons"
import { InfoModal } from "@/components/ui/info-modal"
import { BloodTrackingComparison } from "@/components/ui/blood-tracking-comparison"
import { BookingCta } from "@/components/ui/booking-cta"
import { buttonVariants, ArrowRightIcon } from "@/components/ui/button"
import { membershipBookingAppUrl } from "@/lib/booking"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"

/** Hand-format a positive integer with Swiss apostrophe thousands separators.
 *  Uses the curly apostrophe (’ U+2019) to match `formatNumber` (Intl de-CH)
 *  used everywhere else on the site — e.g. the MRI package cards on this same
 *  page — so a straight ' next to a curly ’ doesn't read as mismatched type.
 *  Hand-rolled (vs Intl) so it's deterministic across Node and browser and SSR
 *  matches hydration. */
function formatChfPrice(n: number): string {
  return Math.round(n)
    .toString()
    .replace(/\B(?=(\d{3})+(?!\d))/g, "’")
}

/**
 * Compact "member vs one-off" modal for a test with a member rate (full-body
 * MRI, omega, hormones, DEXA, body composition). The blood test (the membership
 * entry product) opens the richer side-by-side comparison instead.
 */
function MemberPriceModalContent({
  test,
  title,
  bookingProductId,
  trackingLocation,
  livePrice,
  liveMemberChf,
}: {
  test: TestData
  title: string
  bookingProductId?: string
  trackingLocation: string
  /** Live Stripe regular price (CHF); falls back to `test.price`. */
  livePrice?: number
  /** Live Stripe member price (CHF); falls back to `test.memberPrice`. */
  liveMemberChf?: number | null
}) {
  const tt = useTranslations("tests")
  const ut = useTranslations("ui")
  const locale = useLocale()
  const lp = useLocalePath()
  const { code: country } = useCountry()
  // The membership ask inside the member-price modal. Membership-first markets
  // (CH + AT) lead with "Book check-up" — the concluded winner of the main-CTA
  // copy experiment (see lib/experiments.ts), matching the navbar; NL/DE keep
  // the testing-first fallback.
  const isMembershipFirst = isAllowedInCountry(
    "feature:membership-first-positioning",
    country
  )
  return (
    <div>
      <h2 className="type-headline-md text-neutral-900">{title}</h2>

      {/* Price rows — member rate highlighted, one-off below */}
      <div className="mt-6 flex flex-col gap-2">
        <div className="bg-background flex items-baseline justify-between rounded-[12px] px-4 py-3">
          <span className="type-body-md text-neutral-600">
            {tt("memberPriceLabel")}
          </span>
          <span className="type-title-md text-neutral-900">
            CHF {formatChfPrice(liveMemberChf ?? (test.memberPrice as number))}
          </span>
        </div>
        <div className="flex items-baseline justify-between rounded-[12px] border border-neutral-200 px-4 py-3">
          <span className="type-body-md text-neutral-600">
            {tt("oneOffLabel")}
          </span>
          <span className="type-title-md text-neutral-900">
            CHF {formatChfPrice(livePrice ?? test.price)}
          </span>
        </div>
      </div>

      <p className="type-body-md mt-4 text-neutral-600">
        {tt("memberPriceModalIncludes")}
      </p>

      {/* CTAs — become a member (routes through the booking app's membership
          flow so it feeds the membership-signup metric) + book this test once */}
      <div className="mt-6 flex flex-col gap-3">
        <BookingCta
          overrideHref={membershipBookingAppUrl(locale)}
          trackingLabel="test_member_modal_membership"
          trackingLocation={trackingLocation}
          className={cn(buttonVariants({ variant: "primary" }), "w-full")}
        >
          {isMembershipFirst
            ? ut("mainCtaStartCheckup")
            : tt("memberPriceModalMemberCta")}
        </BookingCta>
        {/* one-off CTA — `bookingProductId` may be undefined (same as the hero
            "Book check-up" CTA), in which case booking opens its generic flow */}
        <BookingCta
          productId={bookingProductId}
          trackingLabel="test_member_modal_oneoff"
          trackingLocation={trackingLocation}
          className={cn(buttonVariants({ variant: "outline" }), "w-full")}
        >
          {tt("memberPriceModalOneOffCta")}
        </BookingCta>
      </div>

      <Link
        href={lp("/membership")}
        className="group type-body-md text-aubergine-800 hover:text-aubergine-900 mt-4 inline-flex items-center gap-1.5 font-medium"
      >
        {tt("memberPriceModalLearnMore")}
        <ArrowRightIcon className="transition-transform duration-200 ease-[cubic-bezier(0.4,0,0.2,1)] group-hover:translate-x-1" />
      </Link>
    </div>
  )
}

/**
 * Hero price block for test detail pages.
 *
 * Testing-first markets (or any test with no member rate): the plain
 * "From CHF {price}" the page has always shown. In membership markets:
 *  - membership entry product (advanced blood test): leads with the annual
 *    membership price + an (i) icon → the membership-vs-one-off comparison.
 *  - test with a member rate: leads with the member price + an (i) icon → a
 *    compact member-vs-one-off modal with membership CTAs.
 *
 * `theme` matches the two heroes: "dark" = mobile hero over the image,
 * "light" = desktop hero on white.
 */
export function TestHeroPrice({
  test,
  membershipFirst,
  showPrice,
  theme,
  bookingProductId,
  trackingLocation,
  livePrice,
  liveMemberChf,
}: {
  test: TestData
  membershipFirst: boolean
  showPrice: boolean
  theme: "light" | "dark"
  /** Resolved Supabase product id for the one-off "book once" CTA. */
  bookingProductId?: string
  /** PostHog attribution prefix for the modal CTAs. */
  trackingLocation: string
  /** Live Stripe regular price (CHF). Preferred over the static `test.price`;
   *  falls back to it when absent. */
  livePrice?: number
  /** Live Stripe member price (CHF). Preferred over the static
   *  `test.memberPrice`; falls back to it when null. */
  liveMemberChf?: number | null
}) {
  const tt = useTranslations("tests")
  const [open, setOpen] = useState(false)
  if (!showPrice) return null

  const dark = theme === "dark"
  const fromCls = cn(
    "text-sm leading-5",
    dark ? "text-white/60" : "text-neutral-900"
  )
  const priceCls = cn(
    "text-2xl leading-8 font-semibold",
    dark ? "text-white" : "text-neutral-900"
  )

  const member = membershipFirst && test.membershipEntry
  const memberRate =
    membershipFirst &&
    !test.membershipEntry &&
    typeof test.memberPrice === "number"

  // Control, or a test we have no member rate for: unchanged plain price.
  if (!member && !memberRate) {
    return (
      <div className="flex items-baseline gap-1">
        <span className={fromCls}>{tt("from")}</span>
        <span className={priceCls}>
          CHF {formatChfPrice(livePrice ?? test.price)}
        </span>
      </div>
    )
  }

  const keyPrefix = TEST_KEY_PREFIX[test.slug] || ""
  const title = tt(`${keyPrefix}Title` as never)

  return (
    <div className="flex items-center gap-2">
      <span className={fromCls}>{tt("from")}</span>
      <span className={priceCls}>
        {member ? (
          <>
            CHF {formatChfPrice(MEMBERSHIP_ANNUAL_CHF)}
            {tt("perYearSuffix")}
          </>
        ) : (
          <>
            CHF {formatChfPrice(liveMemberChf ?? (test.memberPrice as number))}
          </>
        )}
      </span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="-m-[9px] ml-0 inline-flex min-h-[40px] min-w-[40px] flex-shrink-0 cursor-pointer items-center justify-center rounded-full"
        aria-label="Membership pricing details"
      >
        <InfoIcon size={22} />
      </button>
      <InfoModal
        open={open}
        onClose={() => setOpen(false)}
        label={title}
        maxWidth={member ? 1040 : 520}
      >
        {member ? (
          <BloodTrackingComparison
            className="mx-auto max-w-[960px]"
            trackingLocation={trackingLocation}
          />
        ) : (
          <MemberPriceModalContent
            test={test}
            title={title}
            bookingProductId={bookingProductId}
            trackingLocation={trackingLocation}
            livePrice={livePrice}
            liveMemberChf={liveMemberChf}
          />
        )}
      </InfoModal>
    </div>
  )
}
